import {
	Action,
	type CustomerPlan,
	type UserAccessProfile,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import {
	fetchOrganizationCustomerPlansQueryOptions,
	useCreateCustomerPlan,
	useDeleteCustomerPlan,
	useUpdateCustomerPlan,
} from "@/api/customerPlans";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import { ConfirmationWarningModal } from "@/components/ConfirmationWarningModal";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { can } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import {
	CustomerPlanFormModal,
	type CustomerPlanFormValues,
} from "./-components/CustomerPlanFormModal";
import { CustomerPlanList } from "./-components/CustomerPlanList";

/** What the form modal is currently editing: a plan, a new plan, or nothing. */
type FormTarget = CustomerPlan | "create" | null;

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customer-plans/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Preload customer plans data and access profile
		context.queryClient.ensureQueryData(
			fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
		);

		context.queryClient.ensureQueryData(
			fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
		);

		const profile = await context.queryClient.ensureQueryData(
			fetchAccessProfileQueryOptions(userId, token, orgId),
		);

		// Guard: must have VIEW_CUSTOMER_PLANS in this org
		const canView = can(
			profile as UserAccessProfile,
			Action.VIEW_CUSTOMER_PLANS,
		);
		if (!canView) {
			throw new Error("Access denied to customer plans page");
		}

		return { userId, token, orgId, profile };
	},
	component: CustomerPlansPage,
});

function CustomerPlansPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { userId, token, profile } = Route.useLoaderData() as {
		userId: string;
		token: string;
		orgId: string;
		profile: UserAccessProfile;
	};

	const canManage = can(profile, Action.MANAGE_CUSTOMER_PLANS);
	const currentOrganization = useCurrentOrganization();

	const [formTarget, setFormTarget] = useState<FormTarget>(null);
	const [planToDelete, setPlanToDelete] = useState<CustomerPlan | null>(null);

	const { data: customerPlans } = useSuspenseQuery(
		fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
	);

	const { data: establishments } = useSuspenseQuery(
		fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
	);

	const createCustomerPlanMutation = useCreateCustomerPlan();
	const updateCustomerPlanMutation = useUpdateCustomerPlan();
	const deleteCustomerPlanMutation = useDeleteCustomerPlan();

	const editingPlan = formTarget === "create" ? null : formTarget;

	const handleSubmit = async (values: CustomerPlanFormValues) => {
		try {
			if (editingPlan) {
				await updateCustomerPlanMutation.mutateAsync({
					token,
					organizationId: orgId,
					customerPlanId: editingPlan.id,
					data: values,
				});
			} else {
				await createCustomerPlanMutation.mutateAsync({
					token,
					organizationId: orgId,
					data: values,
				});
			}
			// Success toast is handled by the mutation hook
			setFormTarget(null);
		} catch (error) {
			// Error toast is handled by the mutation hook; the modal stays open so
			// the admin keeps everything they typed and can retry.
			console.error("Failed to save customer plan:", error);
		}
	};

	// Delete lives in the edit modal action bar: opening the confirm closes the
	// edit modal so delete has one obvious home per row and avoids stacked modals.
	const handleRequestDelete = () => {
		if (!editingPlan) return;
		setPlanToDelete(editingPlan);
		setFormTarget(null);
	};

	const handleConfirmDelete = async () => {
		if (!planToDelete) return;

		try {
			await deleteCustomerPlanMutation.mutateAsync({
				token,
				organizationId: orgId,
				customerPlanId: planToDelete.id,
			});
			setPlanToDelete(null);
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Error deleting customer plan:", error);
		}
	};

	const isLoading =
		createCustomerPlanMutation.isPending ||
		updateCustomerPlanMutation.isPending ||
		deleteCustomerPlanMutation.isPending;

	return (
		<div className="h-full flex flex-col space-y-4 sm:space-y-6">
			{/* Header */}
			<div
				className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"
				data-testid="customer-plans-header"
			>
				<PageHeader
					title={t("customerPlans.management.title")}
					description={t("customerPlans.management.description")}
					showBackButton={false}
				/>
				{canManage && (
					<Button
						className="btn btn-primary text-primary-content w-full sm:w-auto"
						onClick={() => setFormTarget("create")}
						data-testid="create-customer-plan-button"
					>
						<Plus className="h-4 w-4 mr-2" />
						{t("customerPlans.management.createNew")}
					</Button>
				)}
			</div>

			{/* Content */}
			<div
				className="h-full overflow-auto"
				data-testid="customer-plans-content"
			>
				<CustomerPlanList
					customerPlans={customerPlans}
					establishments={establishments}
					canManage={canManage}
					onEdit={setFormTarget}
					onCreate={() => setFormTarget("create")}
					isLoading={isLoading}
				/>
			</div>

			{/* Create / Edit form. Keyed so each target seeds its own state. */}
			{formTarget !== null && (
				<CustomerPlanFormModal
					key={formTarget === "create" ? "create" : formTarget.id}
					open
					onOpenChange={(open) => {
						if (!open) setFormTarget(null);
					}}
					plan={editingPlan}
					establishments={establishments}
					defaultCurrency={currentOrganization.defaultCurrency}
					isSubmitting={isLoading}
					onSubmit={handleSubmit}
					onDelete={editingPlan && canManage ? handleRequestDelete : undefined}
				/>
			)}

			{/* Delete Confirmation */}
			<ConfirmationWarningModal
				isOpen={!!planToDelete}
				onClose={() => setPlanToDelete(null)}
				onConfirm={handleConfirmDelete}
				title={t("customerPlans.management.actions.confirmDelete")}
				subtitle={
					planToDelete?.name ??
					t("customerPlans.management.actions.confirmDeleteDescription")
				}
				warningTitle={t("customerPlans.management.actions.confirmDelete")}
				warningMessage={t(
					"customerPlans.management.actions.confirmDeleteWarning",
				)}
				confirmButtonText={t("customerPlans.management.actions.delete")}
				loadingText={t("common.deleting")}
				isLoading={deleteCustomerPlanMutation.isPending}
				variant="danger"
			/>
		</div>
	);
}
